import { ApolloQueryResult, FetchPolicy } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';
import { ICurrentFilter } from '@starryui10/filters/filterBar';
import { INoDataOverlayInfo } from '@starryui10/overlays/NoDataOverlay';
import { entryDirectoryCurrentPage, entryDirectoryPageAmount, entryDirectorySearchFor } from '../cache';
import { DirectoryFieldsEnum, IDirectoryVariables, IMainDirectoryQueryResults, ViewTypeEnum } from '../graphql/useMainDirectoryQuery';
import { GetFilterName } from '../utils/stringUtil';
import { columnFormat, loadingDirectory } from './Observables';
import { buildDirectoryFilter, covertGridFormat, getSearchString, noResultsMessage } from './stateUtils';

export type Workspace = {
    initialLoading: boolean;

    data?: IMainDirectoryQueryResults;

    currentFilter?: ICurrentFilter[];
    gridFormat?: global.GridFormat[];

    queryParams: IDirectoryVariables;
    noResultsMessage: INoDataOverlayInfo | null;
};

export type ReducerAction =
    | { type: 'LOAD_DIRECTORY' }
    | { type: 'LOAD_PAGE'; skip: number; limit: number }
    | { type: 'CHANGE_WORKSPACE'; id: string; view: ViewTypeEnum | undefined }
    | { type: 'SAVE_WORKSPACE'; id: string }
    | { type: 'RELOAD' }
    | { type: 'FILTER_GRID'; currentFilter: ICurrentFilter[] }
    | { type: 'CLEAR_FILTERS' }
    | { type: 'SEARCH'; search: string }
    | { type: 'SHOW_SELECTED'; showSelected: boolean }
    | { type: 'CLEAR_SELECTED' }
    | { type: 'SORT'; key: DirectoryFieldsEnum; order: global.SortOrderEnum }
    | { type: 'COLUMNS_RESET_TO_DEFAULT' };

export default async function reducer(
    state: Workspace,
    action: ReducerAction,
    get: (variables: IDirectoryVariables, fetchPolicy?: FetchPolicy) => Promise<ApolloQueryResult<IMainDirectoryQueryResults>>
): Promise<Workspace> {
    return new Promise<Workspace>((resolve) => {
        const newState: Workspace = cloneDeep(state);

        const loadNewWorkspace = (data: IMainDirectoryQueryResults): Workspace => {
            const filter = data?.mainDirectoryWorkspace?.filters?.map<ICurrentFilter>((e) => ({
                name: GetFilterName(e.key),
                value: e.values.map((v) => ({
                    caption: v.description,
                    value: v.itemId,
                })),
            }));

            columnFormat(covertGridFormat(data?.mainDirectoryWorkspace?.gridFormat));

            return {
                initialLoading: false,
                currentFilter: filter || [],
                gridFormat: covertGridFormat(data?.mainDirectoryWorkspace?.gridFormat),
                data,
                queryParams: {
                    ...newState.queryParams,
                    loadWorkSpace: true,
                },
                noResultsMessage: getNoResultsMessage(data),
            };
        };

        const getFiltersSubset = (subset: 'NOT_SEARCH' | 'NOT_FILTERS' | 'NOT_SELECTED_ONLY') => {
            switch (subset) {
                case 'NOT_FILTERS':
                    return newState.queryParams.filter?.filter((f) => f.search || f.selectedItemsOnly);
                case 'NOT_SEARCH':
                    return newState.queryParams.filter?.filter((f) => !f.search);
                case 'NOT_SELECTED_ONLY':
                    return newState.queryParams.filter?.filter((f) => !f.selectedItemsOnly);
                default:
                    break;
            }
        };

        const getNoResultsMessage = (data: IMainDirectoryQueryResults) => {
            if (data.allMainDirectory.totalCount === 0) {
                const searchString = getSearchString(newState.queryParams.filter);
                return noResultsMessage(newState.queryParams.filter, searchString);
            }

            return null;
        };

        switch (action.type) {
            case 'LOAD_DIRECTORY':
                newState.queryParams = {
                    skip: 0,
                    limit: entryDirectoryPageAmount(),
                    sort: [{ field: 'NAME_LAST', order: 'ASC' }],
                    loadWorkSpace: true,
                };

                get(newState.queryParams).then(({ data }) => {
                    resolve({
                        ...newState,
                        ...loadNewWorkspace(data),
                    });
                });

                break;

            case 'LOAD_PAGE':
                loadingDirectory(true);
                entryDirectoryPageAmount(action.limit);

                newState.queryParams = {
                    ...newState.queryParams,
                    skip: action.skip,
                    limit: action.limit,
                };

                get(newState.queryParams).then(({ data }) => {
                    loadingDirectory(false);

                    newState.data = data;
                    newState.noResultsMessage = getNoResultsMessage(data);
                    resolve(newState);
                });
                break;

            case 'RELOAD':
                loadingDirectory(true);

                newState.queryParams.skip = 0;
                entryDirectoryCurrentPage(1);

                get(newState.queryParams, 'network-only').then(({ data }) => {
                    loadingDirectory(false);

                    newState.data = data;
                    newState.noResultsMessage = getNoResultsMessage(data);
                    resolve(newState);
                });
                break;

            case 'CHANGE_WORKSPACE':
                loadingDirectory(true);

                newState.queryParams = {
                    ...newState.queryParams,
                    viewId: Number(action.id),
                    viewType: action.view,
                    filter: [],
                    skip: 0,
                };

                entryDirectoryCurrentPage(1);

                get(newState.queryParams, 'network-only').then(({ data }) => {
                    loadingDirectory(false);
                    resolve({
                        ...newState,
                        ...loadNewWorkspace(data),
                    });
                });
                break;

            case 'SAVE_WORKSPACE':
                loadingDirectory(true);

                newState.queryParams = {
                    ...newState.queryParams,
                    viewId: Number(action.id),
                    viewType: 'DYNAMIC_LIST',
                };

                get(newState.queryParams).then(({ data }) => {
                    loadingDirectory(false);

                    newState.data = data;
                    newState.gridFormat = columnFormat();
                    resolve(newState);
                });
                break;

            case 'SEARCH':
                entryDirectorySearchFor(action.search);

                newState.queryParams.filter = getFiltersSubset('NOT_SEARCH')
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      [...(getFiltersSubset('NOT_SEARCH') as any), { search: { like: action.search } }]
                    : [{ search: { like: action.search } }];

                newState.queryParams.skip = 0;
                entryDirectoryCurrentPage(1);

                get(newState.queryParams)
                    .then(({ data }) => {
                        newState.data = data;
                        newState.gridFormat = columnFormat();
                        newState.noResultsMessage = getNoResultsMessage(data);
                        resolve(newState);
                    })
                    .catch((err) => {
                        throw err;
                    });
                break;

            case 'FILTER_GRID':
                newState.queryParams.filter = getFiltersSubset('NOT_FILTERS')
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      [...buildDirectoryFilter(action.currentFilter), ...(getFiltersSubset('NOT_FILTERS') as any)]
                    : buildDirectoryFilter(action.currentFilter);
                
                newState.queryParams.skip = 0;
                entryDirectoryCurrentPage(1);

                get(newState.queryParams)
                    .then(({ data }) => {
                        newState.currentFilter = action.currentFilter;
                        newState.data = data;
                        newState.gridFormat = columnFormat();
                        newState.noResultsMessage = getNoResultsMessage(data);
                        resolve(newState);
                    })
                    .catch((err) => {
                        throw err;
                    });
                break;

            case 'CLEAR_FILTERS':
                newState.queryParams.filter = getFiltersSubset('NOT_FILTERS') ? getFiltersSubset('NOT_FILTERS') : [];
                newState.queryParams.skip = 0;
                entryDirectoryCurrentPage(1);

                get(newState.queryParams).then(({ data }) => {
                    newState.currentFilter = [];
                    newState.data = data;
                    newState.gridFormat = columnFormat();
                    newState.noResultsMessage = getNoResultsMessage(data);
                    resolve(newState);
                });
                break;

            case 'SHOW_SELECTED':
                newState.queryParams.filter = getFiltersSubset('NOT_SELECTED_ONLY')
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      [...(getFiltersSubset('NOT_SELECTED_ONLY') as any), { selectedItemsOnly: { eq: action.showSelected } }]
                    : [{ selectedItemsOnly: { eq: action.showSelected } }];

                get(newState.queryParams).then(({ data }) => {
                    newState.data = data;
                    newState.gridFormat = columnFormat();
                    resolve(newState);
                });
                break;

            case 'CLEAR_SELECTED':
                newState.queryParams.filter = getFiltersSubset('NOT_SELECTED_ONLY') ? getFiltersSubset('NOT_SELECTED_ONLY') : [];

                get(newState.queryParams).then(({ data }) => {
                    const newData = data.allMainDirectory.nodes.map((node) => ({
                        ...node,
                        isSelected: false,
                    }));

                    newState.data!.allMainDirectory.nodes = newData;
                    newState.gridFormat = columnFormat();
                    resolve(newState);
                });
                break;

            case 'SORT':
                loadingDirectory(true);
                newState.queryParams.sort = [{ field: action.key, order: action.order }];

                get(newState.queryParams).then(({ data }) => {
                    loadingDirectory(false);
                    newState.data = data;
                    resolve(newState);
                });

                break;

            case 'COLUMNS_RESET_TO_DEFAULT':
                resolve({
                    ...newState,
                    gridFormat: [],
                });

                break;

            default:
                throw Error(`${(action as ReducerAction).type} Not found`);
        }
    });
}
